import type {
  WorkflowFormValidationPolicy,
  WorkflowSaveCapability,
  WorkflowWorkItemBridge,
} from '@jukun/platform-sdk/workflow';
import type {
  PortalWorkItemAction,
  PortalWorkItemDetail,
} from '../model/myTasks';

export const WORK_ITEM_CORE_ACTION_TIMEOUT_MS = 30_000;

export class WorkItemSourceSaveFailure extends Error {
  constructor(readonly reason: unknown) {
    super('来源表单保存失败');
    this.name = 'WorkItemSourceSaveFailure';
  }
}

export type WorkItemActionIntent = Readonly<{
  action: PortalWorkItemAction;
  formValidationPolicy: WorkflowFormValidationPolicy;
  opinion?: string;
  targetUserPublicId?: string;
  opinionAttachmentPublicIds: readonly string[];
}>;

export type WorkItemActionValidationGrant = Readonly<{
  actionCode: string;
  traceId: string;
}>;

export type WorkItemActionHost = Readonly<{
  mountId: string;
  taskId: string;
  status: 'ready';
  saveCapability: WorkflowSaveCapability;
  bridge?: WorkflowWorkItemBridge;
}>;

export function workflowActionNeedsDialog(action: PortalWorkItemAction) {
  return action.operationType === 'WORKFLOW';
}

export type WorkItemCoreActionRequest = Readonly<{
  actionCode: string;
  expectedTaskVersion: number;
  opinion?: string;
  targetUserPublicId?: string;
  opinionAttachmentPublicIds: readonly string[];
  operationId: string;
}>;

type Attempt = {
  fingerprint: string;
  operationId: string;
  traceId: string;
  running?: Promise<PortalWorkItemDetail>;
};

export class WorkItemActionGate {
  private retained?: Attempt;
  private disposed = false;
  private readonly activeControllers = new Set<AbortController>();
  private readonly validationGrants = new WeakSet<WorkItemActionValidationGrant>();

  constructor(private readonly options: {
    host: WorkItemActionHost;
    expectedTaskVersion: number;
    callCore: (
      request: WorkItemCoreActionRequest,
      signal: AbortSignal,
    ) => Promise<PortalWorkItemDetail>;
    isAmbiguousCoreFailure: (error: unknown) => boolean;
    isCurrent?: () => boolean;
    runHostOperation?: (operation: (signal: AbortSignal) => Promise<PortalWorkItemDetail>) => Promise<PortalWorkItemDetail>;
    setUncertain?: (value: boolean) => void;
    createOperationId?: () => string;
    createTraceId?: () => string;
    coreTimeoutMs?: number;
  }) {}

  async validate(
    action: PortalWorkItemAction,
    policy: WorkflowFormValidationPolicy,
  ): Promise<WorkItemActionValidationGrant | undefined> {
    this.assertCurrent(action, policy);
    if (policy === 'SKIP') return undefined;
    const bridge = this.options.host.bridge;
    if (!bridge || typeof bridge.validate !== 'function') {
      throw new Error('当前来源表单未注册完整性校验能力');
    }
    const controller = this.track(new AbortController());
    const traceId = (this.options.createTraceId ?? (() => crypto.randomUUID()))();
    try {
      try {
        await bridge.validate({ actionCode: action.actionCode, traceId }, {
          signal: controller.signal,
        });
      } catch (reason) {
        throw new WorkItemSourceSaveFailure(reason);
      }
      this.assertNotDisposed();
      const grant = Object.freeze({ actionCode: action.actionCode, traceId });
      this.validationGrants.add(grant);
      return grant;
    } finally {
      this.activeControllers.delete(controller);
    }
  }

  execute(
    intent: WorkItemActionIntent,
    validationGrant?: WorkItemActionValidationGrant,
  ): Promise<PortalWorkItemDetail> {
    this.assertCurrent(intent.action, intent.formValidationPolicy);
    const traceId = this.consumeValidationGrant(intent, validationGrant);
    const fingerprint = actionFingerprint(
      this.options.host.taskId,
      this.options.expectedTaskVersion,
      intent,
    );
    if (this.retained?.running) {
      if (this.retained.fingerprint === fingerprint) return this.retained.running;
      throw new Error('已有任务动作正在提交，请等待当前请求完成');
    }
    if (!this.retained || this.retained.fingerprint !== fingerprint) {
      this.retained = {
        fingerprint,
        operationId: (this.options.createOperationId ?? (() => crypto.randomUUID()))(),
        traceId,
      };
    }
    const attempt = this.retained;
    const running = this.run(attempt, intent);
    attempt.running = running;
    void running.then(
      () => {
        if (validationGrant) this.validationGrants.delete(validationGrant);
        if (this.retained === attempt) this.retained = undefined;
      },
      () => {
        if (attempt.running === running) attempt.running = undefined;
      },
    );
    return running;
  }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.activeControllers.forEach(controller => controller.abort());
    this.activeControllers.clear();
    this.retained = undefined;
  }

  private async run(
    attempt: Attempt,
    intent: WorkItemActionIntent,
  ): Promise<PortalWorkItemDetail> {
    return this.options.runHostOperation
      ? this.options.runHostOperation(signal => this.runOwned(attempt, intent, signal))
      : this.runOwned(attempt, intent);
  }

  private async runOwned(attempt: Attempt, intent: WorkItemActionIntent,
    sessionSignal?: AbortSignal): Promise<PortalWorkItemDetail> {
    const actionCode = intent.action.actionCode;
    const controller = this.track(new AbortController());
    const abort = () => controller.abort();
    if (sessionSignal?.aborted) abort();
    else sessionSignal?.addEventListener('abort', abort, { once: true });
    try {
      if (this.options.host.saveCapability === 'REQUIRED') {
        const bridge = this.options.host.bridge;
        if (!bridge) throw new Error('当前来源表单保存能力已失效，请重新加载');
        try {
          await bridge.save({
            operationId: attempt.operationId,
            actionCode,
            traceId: attempt.traceId,
          }, { signal: controller.signal });
        } catch (reason) {
          if (this.options.isAmbiguousCoreFailure(reason)) this.options.setUncertain?.(true);
          throw new WorkItemSourceSaveFailure(reason);
        }
        this.assertNotDisposed();
      }
      const request: WorkItemCoreActionRequest = Object.freeze({
        actionCode,
        expectedTaskVersion: this.options.expectedTaskVersion,
        opinion: intent.opinion,
        targetUserPublicId: intent.targetUserPublicId,
        opinionAttachmentPublicIds: [...intent.opinionAttachmentPublicIds],
        operationId: attempt.operationId,
      });
      const result = await callWithinBudget(
        signal => this.options.callCore(request, signal),
        controller,
        this.options.coreTimeoutMs ?? WORK_ITEM_CORE_ACTION_TIMEOUT_MS,
      );
      this.assertNotDisposed();
      if (result.summary.taskId !== this.options.host.taskId) {
        this.options.setUncertain?.(true);
        throw new Error('任务动作响应身份与当前工作项不匹配');
      }
      this.options.setUncertain?.(false);
      return result;
    } catch (error) {
      if (!this.disposed && reachedCore(error)
          && this.options.isAmbiguousCoreFailure(error.cause)) this.options.setUncertain?.(true);
      if (!this.disposed
          && reachedCore(error)
          && !this.options.isAmbiguousCoreFailure(error.cause)
          && this.retained === attempt) {
        this.retained = undefined;
      }
      throw unwrapCoreFailure(error);
    } finally {
      sessionSignal?.removeEventListener('abort', abort);
      this.activeControllers.delete(controller);
    }
  }

  private assertCurrent(
    action: PortalWorkItemAction,
    policy: WorkflowFormValidationPolicy,
  ) {
    this.assertNotDisposed();
    if (action.actionCode === 'SAVE' || action.operationType !== 'WORKFLOW') {
      throw new Error('当前操作不是正式 Workflow 动作');
    }
    if (policy !== 'REQUIRED' && policy !== 'SKIP') {
      throw new Error('当前 Workflow 动作缺少可信完整性校验策略');
    }
    if (!this.options.host.taskId || !this.options.host.mountId) {
      throw new Error('当前来源表单挂载身份无效');
    }
    if (this.options.host.saveCapability === 'REQUIRED'
        && !this.options.host.bridge) {
      throw new Error('当前来源表单未注册保存能力');
    }
    if (this.options.host.saveCapability === 'REQUIRED'
        && typeof this.options.host.bridge?.validate !== 'function') {
      throw new Error('当前来源表单未注册完整性校验能力');
    }
  }

  private consumeValidationGrant(
    intent: WorkItemActionIntent,
    grant?: WorkItemActionValidationGrant,
  ) {
    if (intent.formValidationPolicy === 'SKIP') {
      if (grant) throw new Error('SKIP 动作不得携带完整性校验凭据');
      return (this.options.createTraceId ?? (() => crypto.randomUUID()))();
    }
    if (!grant
        || !this.validationGrants.has(grant)
        || grant.actionCode !== intent.action.actionCode) {
      throw new Error('当前 Workflow 动作尚未完成表单完整性校验');
    }
    return grant.traceId;
  }

  private track(controller: AbortController) {
    this.activeControllers.add(controller);
    return controller;
  }

  private assertNotDisposed() {
    if (this.disposed || this.options.isCurrent?.() === false) throw new DOMException('当前来源表单挂载已过期', 'AbortError');
  }
}

class CoreActionFailure {
  constructor(readonly cause: unknown) {}
}

function reachedCore(error: unknown): error is CoreActionFailure {
  return error instanceof CoreActionFailure;
}

function unwrapCoreFailure(error: unknown) {
  return reachedCore(error) ? error.cause : error;
}

async function callWithinBudget<T>(
  call: (signal: AbortSignal) => Promise<T>,
  controller: AbortController,
  timeoutMs: number,
) {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    throw new Error('Core 动作超时配置不合法');
  }
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const raw = Promise.resolve().then(() => call(controller.signal));
  void raw.catch(() => undefined);
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort();
      reject(new DOMException('Core 动作结果未知，请使用原请求重试', 'TimeoutError'));
    }, timeoutMs);
  });
  try {
    return await Promise.race([raw, timeout]);
  } catch (error) {
    throw new CoreActionFailure(error);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

function actionFingerprint(
  taskId: string,
  expectedTaskVersion: number,
  intent: WorkItemActionIntent,
) {
  return JSON.stringify({
    taskId,
    expectedTaskVersion,
    actionCode: intent.action.actionCode,
    formValidationPolicy: intent.formValidationPolicy,
    opinion: intent.opinion ?? '',
    targetUserPublicId: intent.targetUserPublicId ?? '',
    opinionAttachmentPublicIds: [...intent.opinionAttachmentPublicIds],
  });
}
